import React, { useState, useRef } from 'react';
import { AccordionItemHeading, AccordionItemButton, AccordionItemPanel } from 'react-accessible-accordion';
import { IconButton, Tooltip } from '@mui/material';
import { DataObject as DataIcon, PlayArrow, Pause, VolumeUp } from '@mui/icons-material';
import Actions from './props/Actions';

const Call = (props) => {
    let { activity, activityData, onUpdateActivity } = props;
    let [hovered, setHovered] = useState(false);

    // Audio player state
    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);

    // Get call log data
    const callLog = activity?.callLogId;
    const recordingUrl = callLog?.recordingUrl;
    const callTranscript = callLog?.callTranscript;
    const callType = callLog?.callType;
    const callLogStatus = callLog?.status;
    const callLogCreatedAt = callLog?.createdAt;
    const endReason = callLog?.endReason;

    // Format call type for display
    const formatCallType = (type) => {
        const types = { 'outbound_call': 'Outbound', 'inbound_call': 'Inbound', 'cold_calling': 'Cold Call', 'voice_call': 'Voice Call'};
        return types[type] || type;
    };

    // Format date/time for display
    const formatDateTime = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toLocaleString('en-US', {  month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true});
    };

    // Audio player functions
    const togglePlay = (e) => {
        e.stopPropagation();
        if (audioRef.current) {
            if (isPlaying) audioRef.current.pause();
            else audioRef.current.play();
            setIsPlaying(!isPlaying);
        }
    };

    const handleTimeUpdate = () => {
        if (audioRef.current) setCurrentTime(audioRef.current.currentTime);
    };

    const handleLoadedMetadata = () => {
        if (audioRef.current) setDuration(audioRef.current.duration);
    };

    const handleSeek = (e) => {
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const percentage = x / rect.width;
        if (audioRef.current) audioRef.current.currentTime = percentage * duration;
    };

    const formatTime = (time) => {
        if (!time || isNaN(time)) return '0:00';
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };


    const getCallDirection = () => {
        if (activity?.direction) return activity.direction === 'outbound' ? 'Outgoing' : 'Incoming';
        return activity?.metadata?.automated ? 'Outgoing (Automated)' : 'Call';
    };

    return (
        <>
            <AccordionItemHeading>
                <AccordionItemButton>
                    <div className="activity-card call-card">
                        <div className="activity-header">
                            <div className="activity-type-info">
                                <div className="activity-icon call-icon">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M22 16.92V19.92C22 20.52 21.52 21 21 21C9.96 21 1 12.04 1 1C1 0.48 1.48 0 2 0H5C5.52 0 6 0.48 6 1C6 2.28 6.22 3.5 6.62 4.62C6.78 5.08 6.64 5.58 6.26 5.96L4.25 7.97C5.83 11.24 8.76 14.17 12.03 15.75L14.04 13.74C14.42 13.36 14.92 13.22 15.38 13.38C16.5 13.78 17.72 14 19 14C19.52 14 20 14.48 20 15V18C20 18.52 19.52 19 19 19Z" fill="currentColor" />
                                    </svg>
                                </div>
                                <div className="activity-details">
                                    <div className="activity-title call-card-title">
                                        <span className="call-card-subject">
                                            {activity?.phoneNumber || activity?.metadata?.recipient || activity?.title || 'Phone call'}
                                        </span>
                                        <span className="call-card-direction">{getCallDirection()}</span>
                                    </div>
                                    <div className="activity-meta">
                                        <span className="created-by">by {activityData?.createdBy}</span>
                                        {activity?.duration && (
                                            <span className="call-duration">{activity.duration}min</span>
                                        )}
                                        <div className="activity-badges">
                                            {activity?.metadata?.source && (() => {
                                                const meta = getCallSourceMeta(activity.metadata.source);
                                                return (
                                                    <span className="activity-card-source" style={{ color: '#6c757d', fontSize: '11px', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                        <i className={meta.icon} style={{ fontSize: '10px' }}></i>{meta.label}
                                                    </span>
                                                );
                                            })()}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="activity-actions" onClick={(e) => e.stopPropagation()}>
                                {props.hasExternalData && (
                                    <Tooltip title={`View ${props.dataSource || 'External'} Data`}>
                                        <IconButton
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                props.onViewData && props.onViewData();
                                            }}
                                            sx={{
                                                color: '#2563EB',
                                                backgroundColor: '#EFF6FF',
                                                '&:hover': {
                                                    backgroundColor: '#DBEAFE'
                                                },
                                                marginRight: '8px'
                                            }}
                                        >
                                            <DataIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                <Actions {...props} />
                                <span className="activity-time">{activityData?.createdAt}</span>
                            </div>
                        </div>

                        {activity?.description && (
                            <div className="activity-card-preview">{stripHtml(activity.description)}</div>
                        )}
                    </div>
                </AccordionItemButton>
            </AccordionItemHeading>

            <AccordionItemPanel>
                <div className="activity-card-body call-card-body">
                    {activity?.description && (
                        <div className="activity-description" dangerouslySetInnerHTML={{ __html: activity.description }}></div>
                    )}

                    {activity?.metadata && (
                        <div className="activity-metadata">
                            {activity.metadata.callId && (
                                <div className="metadata-item">
                                    <strong>Call ID:</strong> {activity.metadata.callId}
                                </div>
                            )}
                            {activity.metadata.workflowId && (
                                <div className="metadata-item">
                                    <strong>Via Workflow</strong>
                                    <span className="workflow-badge">Automated</span>
                                </div>
                            )}
                        </div>
                    )}

                    {activity?.outcome && (
                        <div className="call-outcome">
                            <strong>Outcome:</strong> {activity.outcome}
                        </div>
                    )}

                    {callTranscript && (
                        <div className="call-transcript-section" onClick={(e) => e.stopPropagation()}>
                            <div className="transcript-content">{callTranscript}</div>
                        </div>
                    )}

                    {callLog && (
                        <div className="call-info-row">
                            <div className="call-info-column">
                                <span className="call-info-label">Outcome</span>
                                <span className="call-info-value outcome-value">{endReason || 'N/A'}</span>
                            </div>
                            <div className="call-info-column">
                                <span className="call-info-label">Direction</span>
                                <span className="call-info-value direction-value">{formatCallType(callType) || 'N/A'}</span>
                            </div>
                            <div className="call-info-column">
                                <span className="call-info-label">Date & Time</span>
                                <span className="call-info-value datetime-value">{formatDateTime(callLogCreatedAt) || 'N/A'}</span>
                            </div>
                        </div>
                    )}

                    {recordingUrl && (
                        <div className="call-recording-section" onClick={(e) => e.stopPropagation()}>
                            <div className="recording-label">
                                <VolumeUp fontSize="small" />
                                <span>Review recording</span>
                            </div>
                            <div className="audio-player">
                                <audio ref={audioRef} src={recordingUrl} onTimeUpdate={handleTimeUpdate} onLoadedMetadata={handleLoadedMetadata} onEnded={() => setIsPlaying(false)}/>
                                <button className="play-btn" onClick={togglePlay}> {isPlaying ? <Pause fontSize="small" /> : <PlayArrow fontSize="small" />} </button>
                                <div className="progress-container" onClick={handleSeek}>
                                    <div className="progress-bar" style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }} />
                                </div>
                                <span className="time-display">{formatTime(currentTime)} / {formatTime(duration)}</span>
                            </div>
                        </div>
                    )}
                </div>
            </AccordionItemPanel>
        </>
    );
};

const stripHtml = (html) => (html || '')
    .replace(/<style[\s\S]*?<\/style>/gi, '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\s+/g, ' ')
    .trim();

const getCallSourceMeta = (source) => {
    if (source === 'manual') return { icon: 'fa fa-phone', label: 'Manual' };
    if (source === 'campaign') return { icon: 'fa fa-bullhorn', label: 'Campaign' };
    return { icon: 'fa fa-cog', label: 'Workflow' };
};

export default Call;